import { useIsAuthenticated } from '@azure/msal-react';
import {
  useFeatureFlags,
  useFeatureFlagsProviderContext,
} from '@brightdrop/feature-flags-client';
import { Role } from '@gm-commercial/profile-model';
import { Alert, Button, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, matchPath, Navigate, useLocation } from 'react-router-dom';
import TranslatedLabel from '~/common/components/TranslatedLabel/TranslatedLabel';
import { BDRequestStatus } from '~/common/models/apis/apiResponse.model';
import { ADVISOR_TOOL_GMF_FLAG } from '~/common/models/featureFlags.model';
import { AppRoutePaths } from '~/common/models/route.model';
import {
  getGmfAdvisorDestination,
  isGmfAccountPath,
  isGmfRolloutEnabled,
} from '~/common/utils/route/gmfAdvisorPolicy';
import {
  selectActingProfile,
  selectGetProfileOperation,
  selectProfile,
  selectRole,
} from '~/store/profile/profileSlice.selectors';

/** Gates page mounting using the signed-in role, before page-specific requests run. */
const GmfAdvisorBoundary = ({ children }: { children: ReactNode }) => {
  useTranslation('organization');
  const role = useSelector(selectRole);
  const profileRead = useSelector(selectGetProfileOperation);
  const profile = useSelector(selectProfile);
  const actingProfile = useSelector(selectActingProfile);
  const authenticated = useIsAuthenticated();
  const location = useLocation();
  const { pathname, search, hash } = location;
  const { getFlag } = useFeatureFlags();
  const { isUserContextIdentified } = useFeatureFlagsProviderContext();

  if (pathname === '/' || /^\/(?:auth|login|logout)(?:\/|$)/.test(pathname)) {
    return <>{children}</>;
  }
  if (
    pathname === AppRoutePaths.SERVICE_UNAVAILABLE ||
    pathname === AppRoutePaths.MOBILE_APP_REQUIRED
  ) {
    return <>{children}</>;
  }
  if (!role && !authenticated) return <>{children}</>;
  if (
    role !== Role.GMF_ADVISOR &&
    [
      AppRoutePaths.QUICK_PAY,
      AppRoutePaths.QUICK_PAY_PAYMENT,
      AppRoutePaths.QUICK_PAY_CONFIRMATION,
    ].some((path) => matchPath({ path, end: true }, pathname))
  ) {
    return <>{children}</>;
  }
  if (!role) {
    if (profileRead?.status === BDRequestStatus.SUCCEEDED) {
      return (
        <Alert
          severity="error"
          action={
            <Button component={Link} to={AppRoutePaths.LOGOUT} color="inherit">
              <TranslatedLabel translationKey="common:header.signOut" />
            </Button>
          }
        >
          <TranslatedLabel translationKey="common:alertMsg.messageView.unauthorized.header" />
        </Alert>
      );
    }
    const missingProfile =
      profileRead?.status === BDRequestStatus.FAILED &&
      profileRead.errors?.some((error) => error.status === 404);
    if (
      missingProfile &&
      (/^\/business-enrollment(?:\/|$)/.test(pathname) ||
        pathname === '/migration-not-complete')
    ) {
      return <>{children}</>;
    }
    return <Navigate to="/" state={{ SecureRoute: location }} replace />;
  }
  if (role !== Role.GMF_ADVISOR) {
    if (authenticated && !actingProfile) {
      return <Navigate to="/" state={{ SecureRoute: location }} replace />;
    }
    return <>{children}</>;
  }
  if (profile?.status === 'pending') {
    return (
      <Alert
        severity="error"
        action={
          <Button component={Link} to={AppRoutePaths.LOGOUT} color="inherit">
            <TranslatedLabel translationKey="common:header.signOut" />
          </Button>
        }
      >
        <TranslatedLabel translationKey="common:alertMsg.messageView.unauthorized.header" />
      </Alert>
    );
  }

  const enabled = isGmfRolloutEnabled(
    getFlag(ADVISOR_TOOL_GMF_FLAG),
    isUserContextIdentified
  );
  const destination = `${pathname}${search}${hash}`;
  const allowedDestination = getGmfAdvisorDestination(destination, enabled);
  if (isUserContextIdentified && allowedDestination !== destination) {
    return <Navigate to={allowedDestination} replace />;
  }
  if (!enabled && !isGmfAccountPath(pathname)) {
    return (
      <section className="contentInner">
        <Typography variant="header1_medium" component="h1">
          <TranslatedLabel translationKey="organization:organization_plural" />
        </Typography>
      </section>
    );
  }
  return <>{children}</>;
};

export default GmfAdvisorBoundary;
